import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Save, Sparkles, FileDown, Loader2, Share2, Copy, Link2, Ban, Eye, Mail, CheckCircle2, Circle, Check, FileSpreadsheet, Plug, ExternalLink, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { fileUrl, apiError } from "@/lib/api";

const num = (v) => Number(v) || 0;
const gbp = (n) => `£${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const xeroStatusColor = (label) => {
  const l = (label || "").toLowerCase();
  if (l === "paid") return "bg-emerald-100 text-emerald-700";
  if (l === "approved" || l === "part-paid") return "bg-blue-100 text-blue-700";
  if (l === "awaiting approval") return "bg-amber-100 text-amber-700";
  if (l === "voided" || l === "deleted") return "bg-red-100 text-red-600";
  return "bg-slate-100 text-slate-600"; // Draft / default
};

const BillingLedger = ({ projectId }) => {
  const [rows, setRows] = useState([]);
  const [retainage, setRetainage] = useState(5);
  const [mode, setMode] = useState("basic"); // "basic" (% complete) | "advanced" (full SOV)
  const [cisRate, setCisRate] = useState(0);
  const [vatTreatment, setVatTreatment] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [autoState, setAutoState] = useState("idle");
  const [loaded, setLoaded] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [xeroBusy, setXeroBusy] = useState(false);
  const [xeroInvoice, setXeroInvoice] = useState(null); // { url, number, status }
  const [xeroStatus, setXeroStatus] = useState(null); // friendly label from Xero
  const [xeroStatusBusy, setXeroStatusBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [clientEmail, setClientEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/billing`);
    const items = data.line_items || [];
    setRows(items);
    setRetainage(data.retainage_pct ?? 5);
    setCisRate(data.cis_rate ?? 0);
    setVatTreatment(data.vat_treatment || "");
    setNotes(data.notes || "");
    const hasAdvancedData = items.some((r) => num(r.previous_work) || num(r.stored_materials) || (num(r.this_period) && r.percent_complete == null));
    setMode(data.billing_mode || (hasAdvancedData ? "advanced" : "basic"));
    if (data.xero_url) setXeroInvoice({ url: data.xero_url, number: data.xero_invoice_number, status: data.xero_status });
    if (data.xero_status_label) setXeroStatus(data.xero_status_label);
    setLoaded(true);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/integrations/xero/status").then(({ data }) => setXeroConnected(!!data.connected)).catch(() => {}); }, []);

  const loadXeroStatus = useCallback(async () => {
    setXeroStatusBusy(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/billing/xero-status`);
      if (data.has_invoice) {
        if (data.status_label) setXeroStatus(data.status_label);
        if (data.xero_url) setXeroInvoice({ url: data.xero_url, number: data.invoice_number, status: data.status });
      }
    } catch { /* keep last-known */ }
    finally { setXeroStatusBusy(false); }
  }, [projectId]);
  // Refresh the live Xero status once the invoice link is known.
  useEffect(() => {
    if (xeroInvoice?.url && xeroConnected) loadXeroStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xeroInvoice?.url, xeroConnected]);

  // Quietly keep the status badge fresh: poll every 60s and on window/tab focus.
  useEffect(() => {
    if (!xeroInvoice?.url || !xeroConnected) return;
    const id = setInterval(loadXeroStatus, 60000);
    const onFocus = () => { if (document.visibilityState === "visible") loadXeroStatus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xeroInvoice?.url, xeroConnected]);

  const sendToXero = async () => {
    setXeroBusy(true);
    try {
      await api.put(`/projects/${projectId}/billing`, billingBody());
      const { data } = await api.post(`/projects/${projectId}/billing/send-to-xero`);
      const cis = data.cis_deduction ? ` · CIS £${Number(data.cis_deduction).toFixed(2)}` : "";
      if (data.xero_url) setXeroInvoice({ url: data.xero_url, number: data.invoice_number, status: data.status });
      if (data.status_label) setXeroStatus(data.status_label);
      toast.success(`Draft invoice ${data.invoice_number || ""} created in ${data.organisation || "Xero"}${cis} — review & approve it in Xero`);
    } catch (err) { toast.error(err.response?.data?.detail || "Send to Xero failed"); }
    finally { setXeroBusy(false); }
  };

  const billingBody = () => ({
    line_items: mode === "basic"
      ? rows.map((r) => ({ ...r, previous_work: 0, stored_materials: 0, this_period: num(r.scheduled_value) * (num(r.percent_complete) / 100) }))
      : rows,
    retainage_pct: num(retainage), notes, cis_rate: num(cisRate), vat_treatment: vatTreatment, billing_mode: mode,
  });

  useEffect(() => {
    if (!loaded) return;
    setAutoState("saving");
    const t = setTimeout(async () => {
      try { await api.put(`/projects/${projectId}/billing`, billingBody()); setAutoState("saved"); }
      catch { setAutoState("error"); }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, retainage, cisRate, vatTreatment, notes, mode, projectId, loaded]);

  const addRow = () => setRows((r) => [...r, { id: crypto.randomUUID(), description: "", scheduled_value: 0, previous_work: 0, this_period: 0, stored_materials: 0, percent_complete: 0, cis_category: "labour" }]);
  const update = (id, k, v) => setRows((r) => r.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const remove = (id) => setRows((r) => r.filter((x) => x.id !== id));

  const completedOf = (r) => mode === "basic"
    ? num(r.scheduled_value) * (num(r.percent_complete) / 100)
    : num(r.previous_work) + num(r.this_period) + num(r.stored_materials);
  const totalScheduled = rows.reduce((s, r) => s + num(r.scheduled_value), 0);
  const totalCompleted = rows.reduce((s, r) => s + completedOf(r), 0);
  const retainAmt = (totalCompleted * num(retainage)) / 100;
  const netAfterRetention = totalCompleted - retainAmt;
  const labourCompleted = rows.reduce((s, r) => s + ((r.cis_category || "labour") === "labour" ? completedOf(r) : 0), 0);
  const labourNet = labourCompleted * (1 - num(retainage) / 100);
  const cisDeduction = (labourNet * num(cisRate)) / 100;
  const VAT_RATES = { standard_20: 20, reduced_5: 5, zero: 0, exempt: 0, reverse_charge: 0, "": 0 };
  const VAT_LABELS = { standard_20: "VAT (20%)", reduced_5: "VAT (5%)", zero: "VAT (0%)", exempt: "VAT (exempt)", reverse_charge: "VAT — domestic reverse charge", "": "VAT" };
  const vatAmount = (netAfterRetention * (VAT_RATES[vatTreatment] || 0)) / 100;
  const due = netAfterRetention - cisDeduction + vatAmount;
  const balanceToFinish = totalScheduled - totalCompleted;

  const save = async () => {
    setSaving(true);
    try { await api.put(`/projects/${projectId}/billing`, billingBody()); setAutoState("saved"); toast.success("Billing saved"); }
    catch { setAutoState("error"); toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const genNote = async () => {
    setAiLoading(true);
    try {
      await api.put(`/projects/${projectId}/billing`, billingBody());
      const { data } = await api.post(`/projects/${projectId}/billing/ai-note`);
      setNotes(data.note);
      toast.success("AI billing note generated");
    } catch { toast.error("Could not generate note"); }
    finally { setAiLoading(false); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      await api.put(`/projects/${projectId}/billing`, billingBody());
      const { data } = await api.post(`/projects/${projectId}/export`);
      const link = document.createElement("a");
      link.href = fileUrl(data.storage_path);
      link.download = data.filename;
      link.target = "_blank";
      link.click();
      toast.success("PDF exported");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const exportAccountingCsv = async () => {
    setCsvBusy(true);
    try {
      await api.put(`/projects/${projectId}/billing`, billingBody());
      const { data } = await api.get(`/projects/${projectId}/billing/accounting-csv`, { params: { provider: "xero" }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "valuation-xero-invoice.csv";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Accounting CSV ready — import it into Xero, QuickBooks or Sage");
    } catch { toast.error("CSV export failed"); }
    finally { setCsvBusy(false); }
  };

  const openShare = async () => {
    setShareOpen(true);
    try { const { data } = await api.get(`/projects/${projectId}/share`); setShare(data.active ? data : null); setClientEmail(data.active ? (data.sent_to || "") : ""); }
    catch { setShare(null); }
  };

  const sendToClient = async () => {
    if (!clientEmail) return;
    setSending(true);
    try {
      await api.put(`/projects/${projectId}/billing`, billingBody());
      await api.post(`/projects/${projectId}/share/send`, { email: clientEmail, origin_url: window.location.origin });
      const { data } = await api.get(`/projects/${projectId}/share`);
      setShare(data.active ? data : null);
      toast.success(`Application emailed to ${clientEmail}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not send email"); }
    finally { setSending(false); }
  };

  const generateShare = async () => {
    setShareBusy(true);
    try {
      await api.put(`/projects/${projectId}/billing`, billingBody());
      const { data } = await api.post(`/projects/${projectId}/share`);
      setShare(data);
      toast.success("Share link created from current billing");
    } catch { toast.error("Could not create link"); }
    finally { setShareBusy(false); }
  };

  const revokeShare = async () => {
    setShareBusy(true);
    try { await api.post(`/projects/${projectId}/share/revoke`); setShare(null); toast.success("Link revoked"); }
    catch { toast.error("Could not revoke"); }
    finally { setShareBusy(false); }
  };

  const togglePaid = async () => {
    setShareBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/share/mark-paid`, { paid: !share?.paid });
      setShare(data);
      toast.success(data.paid ? "Marked as paid" : "Marked as unpaid");
    } catch { toast.error("Could not update payment status"); }
    finally { setShareBusy(false); }
  };

  const shareUrl = share ? `${window.location.origin}/pay/${share.token}` : "";
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-sm" data-testid="billing-mode-toggle">
            <button onClick={() => setMode("basic")} data-testid="billing-mode-basic" className={`px-2.5 py-1 rounded ${mode === "basic" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>Simple</button>
            <button onClick={() => setMode("advanced")} data-testid="billing-mode-advanced" className={`px-2.5 py-1 rounded ${mode === "advanced" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>Detailed</button>
          </div>
          <Button size="sm" onClick={addRow} data-testid="add-billing-row" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1.5" /> Add line</Button>
          <div className="flex items-center gap-1.5 text-sm text-slate-600 bg-white border border-slate-200 rounded-md px-2 py-1">
            Retainage
            <input type="number" value={retainage} onChange={(e) => setRetainage(e.target.value)} className="w-14 text-right font-mono outline-none" data-testid="retainage-input" /> %
          </div>
          <div className="flex items-center gap-1.5 text-sm text-slate-600 bg-white border border-slate-200 rounded-md px-2 py-1">
            CIS
            <select value={cisRate} onChange={(e) => setCisRate(e.target.value)} className="text-sm outline-none bg-transparent font-mono cursor-pointer" data-testid="cis-rate-select">
              <option value={0}>None (0%)</option>
              <option value={20}>Net (20%)</option>
              <option value={30}>Higher (30%)</option>
            </select>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-slate-600 bg-white border border-slate-200 rounded-md px-2 py-1">
            VAT
            <select value={vatTreatment} onChange={(e) => setVatTreatment(e.target.value)} className="text-sm outline-none bg-transparent cursor-pointer" data-testid="vat-treatment-select">
              <option value="">Not set</option>
              <option value="standard_20">Standard (20%)</option>
              <option value="reduced_5">Reduced (5%)</option>
              <option value="zero">Zero-rated (0%)</option>
              <option value="exempt">Exempt</option>
              <option value="reverse_charge">Domestic reverse charge</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={genNote} disabled={aiLoading} data-testid="ai-note-btn" className="border-blue-300 text-blue-700">
            {aiLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} AI billing note
          </Button>
          <Button size="sm" variant="outline" onClick={openShare} data-testid="share-app-btn" className="border-slate-300"><Share2 className="h-4 w-4 mr-1.5" /> Share with client</Button>
          <Button size="sm" variant="outline" onClick={exportPdf} disabled={exporting} data-testid="export-pdf-btn" className="border-slate-300">
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />} Export PDF
          </Button>
          <Button size="sm" variant="outline" onClick={exportAccountingCsv} disabled={csvBusy} data-testid="export-accounting-csv-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            {csvBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />} Export to accounting (CSV)
          </Button>
          {xeroConnected && (
            <Button size="sm" variant="outline" onClick={sendToXero} disabled={xeroBusy} data-testid="send-to-xero-btn" className="border-[#13B5EA] text-[#0f9fce] hover:bg-sky-50">
              {xeroBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plug className="h-4 w-4 mr-1.5" />} Send to Xero
            </Button>
          )}
          {xeroInvoice?.url && (
            <div className="inline-flex items-center gap-1.5">
              <a href={xeroInvoice.url} target="_blank" rel="noopener noreferrer" data-testid="view-in-xero-link"
                 className="inline-flex items-center h-8 px-3 rounded-md text-sm font-medium border border-[#13B5EA] text-[#0f9fce] bg-sky-50 hover:bg-sky-100 transition-colors"
                 title={xeroInvoice.number ? `Open ${xeroInvoice.number} in Xero` : "Open in Xero"}>
                <ExternalLink className="h-4 w-4 mr-1.5" /> View in Xero{xeroInvoice.number ? ` (${xeroInvoice.number})` : ""}
              </a>
              {xeroStatus && (
                <span data-testid="xero-status-badge" className={`inline-flex items-center h-6 px-2 rounded-full text-xs font-semibold ${xeroStatusColor(xeroStatus)}`}>
                  {xeroStatus}
                </span>
              )}
              {xeroConnected && (
                <button onClick={loadXeroStatus} disabled={xeroStatusBusy} data-testid="xero-status-refresh" title="Refresh status from Xero"
                        className="inline-flex items-center justify-center h-6 w-6 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-50">
                  <RefreshCw className={`h-3.5 w-3.5 ${xeroStatusBusy ? "animate-spin" : ""}`} />
                </button>
              )}
            </div>
          )}
          <Button size="sm" onClick={save} disabled={saving} data-testid="save-billing-btn" className="bg-slate-900 hover:bg-slate-800"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}</Button>
          <span className="text-xs text-slate-400 flex items-center gap-1.5 ml-1" data-testid="billing-autosave-status">
            {autoState === "saving" && <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>}
            {autoState === "saved" && <><Check className="h-3.5 w-3.5 text-green-600" /> Saved</>}
            {autoState === "error" && <button onClick={save} className="text-red-500 flex items-center gap-1.5" data-testid="billing-autosave-retry"><Save className="h-3.5 w-3.5" /> Save failed — retry</button>}
          </span>
        </div>
      </div>

      <div className="flex items-start gap-2 text-sm rounded-lg border px-3 py-2 bg-blue-50/60 border-blue-100 text-slate-600" data-testid="billing-mode-help">
        {mode === "basic"
          ? <p><span className="font-semibold text-slate-800">Simple mode:</span> for each item enter the <span className="font-medium">contract value</span> and how <span className="font-medium">complete</span> it is (%). We work out what to bill this application — including retention{num(cisRate) > 0 ? ", CIS" : ""}{vatTreatment ? " and VAT" : ""}. Switch to <span className="font-medium">Detailed</span> for a full schedule of values (previous / this period / stored).</p>
          : <p><span className="font-semibold text-slate-800">Detailed mode:</span> a full schedule of values — split each item into <span className="font-medium">Previous</span> (billed before), <span className="font-medium">This period</span> and <span className="font-medium">Stored materials</span>. Prefer something simpler? Switch to <span className="font-medium">Simple</span>.</p>}
      </div>

      <div className="border border-slate-200 rounded-lg overflow-x-auto bg-white">
        <table className="w-full ledger-table" data-testid="billing-ledger-table">
          <thead className="bg-slate-50">
            {mode === "basic" ? (
              <tr className="text-xs uppercase tracking-wide text-slate-500 text-left">
                <th className="w-[36%]">Description</th>
                {num(cisRate) > 0 && <th className="w-24">Type</th>}
                <th className="text-right">Contract value</th>
                <th className="w-[26%]">% complete</th>
                <th className="text-right">Completed</th>
                <th className="w-10"></th>
              </tr>
            ) : (
              <tr className="text-xs uppercase tracking-wide text-slate-500 text-left">
                <th className="w-[24%]">Description</th>
                <th className="w-24">Type</th>
                <th className="text-right">Scheduled</th>
                <th className="text-right">Previous</th>
                <th className="text-right">This period</th>
                <th className="text-right">Stored mat.</th>
                <th className="text-right">Completed</th>
                <th className="text-right">Balance</th>
                <th className="w-10"></th>
              </tr>
            )}
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={mode === "basic" ? 6 : 9} className="py-10" data-testid="billing-empty-state">
                <div className="text-center">
                  <FileDown className="h-8 w-8 text-slate-300 mx-auto" />
                  <p className="mt-2 text-slate-500 text-sm">No billing lines yet — add your {mode === "basic" ? "items to start progress billing" : "schedule of values to start progress billing"}.</p>
                  <Button size="sm" onClick={addRow} className="mt-3 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1.5" /> Add line</Button>
                </div>
              </td></tr>
            )}
            {rows.map((r) => {
              const comp = completedOf(r);
              if (mode === "basic") {
                const pct = Math.max(0, Math.min(100, num(r.percent_complete)));
                return (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`billing-row-${r.id}`}>
                    <td><input className="ledger-input" value={r.description} onChange={(e) => update(r.id, "description", e.target.value)} placeholder="Work item" /></td>
                    {num(cisRate) > 0 && (
                      <td>
                        <select className="ledger-input cursor-pointer text-sm" value={r.cis_category || "labour"} onChange={(e) => update(r.id, "cis_category", e.target.value)} data-testid={`cis-category-${r.id}`}>
                          <option value="labour">Labour</option>
                          <option value="materials">Materials</option>
                        </select>
                      </td>
                    )}
                    <td><input type="number" className="ledger-input text-right font-mono" value={r.scheduled_value} onChange={(e) => update(r.id, "scheduled_value", e.target.value)} /></td>
                    <td>
                      <div className="flex items-center gap-2 px-1">
                        <input type="number" min={0} max={100} className="w-16 text-right font-mono outline-none border border-slate-200 rounded px-1.5 py-1" value={r.percent_complete ?? 0} onChange={(e) => update(r.id, "percent_complete", e.target.value)} data-testid={`percent-complete-${r.id}`} />
                        <span className="text-slate-400 text-sm">%</span>
                        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                      </div>
                    </td>
                    <td className="text-right font-mono text-slate-700">{gbp(comp)}</td>
                    <td className="text-center"><button onClick={() => remove(r.id)} className="text-slate-300 hover:text-red-500" data-testid={`remove-billing-${r.id}`}><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                );
              }
              return (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`billing-row-${r.id}`}>
                  <td><input className="ledger-input" value={r.description} onChange={(e) => update(r.id, "description", e.target.value)} placeholder="Work item" /></td>
                  <td>
                    <select className="ledger-input cursor-pointer text-sm" value={r.cis_category || "labour"} onChange={(e) => update(r.id, "cis_category", e.target.value)} data-testid={`cis-category-${r.id}`}>
                      <option value="labour">Labour</option>
                      <option value="materials">Materials</option>
                    </select>
                  </td>
                  <td><input type="number" className="ledger-input text-right font-mono" value={r.scheduled_value} onChange={(e) => update(r.id, "scheduled_value", e.target.value)} /></td>
                  <td><input type="number" className="ledger-input text-right font-mono" value={r.previous_work} onChange={(e) => update(r.id, "previous_work", e.target.value)} /></td>
                  <td><input type="number" className="ledger-input text-right font-mono" value={r.this_period} onChange={(e) => update(r.id, "this_period", e.target.value)} /></td>
                  <td><input type="number" className="ledger-input text-right font-mono" value={r.stored_materials} onChange={(e) => update(r.id, "stored_materials", e.target.value)} /></td>
                  <td className="text-right font-mono text-slate-700">{gbp(comp)}</td>
                  <td className="text-right font-mono text-slate-500">{gbp(num(r.scheduled_value) - comp)}</td>
                  <td className="text-center"><button onClick={() => remove(r.id)} className="text-slate-300 hover:text-red-500" data-testid={`remove-billing-${r.id}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-xs text-slate-400">{mode === "basic" ? "Completed to date" : "Total completed & stored"}</p><p className="font-mono text-lg font-bold text-slate-800">{gbp(totalCompleted)}</p></div>
          <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-xs text-slate-400">Balance to finish</p><p className="font-mono text-lg font-bold text-slate-800">{gbp(balanceToFinish)}</p></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="billing-waterfall">
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-600"><span>{mode === "basic" ? "Completed to date" : "Completed & stored"}</span><span className="font-mono">{gbp(totalCompleted)}</span></div>
            <div className="flex justify-between text-amber-600"><span>Less retainage ({num(retainage)}%)</span><span className="font-mono">- {gbp(retainAmt)}</span></div>
            <div className="flex justify-between text-slate-700 border-t border-slate-100 pt-1.5"><span>Net after retention</span><span className="font-mono">{gbp(netAfterRetention)}</span></div>
            {num(cisRate) > 0 && (
              <div className="flex justify-between text-red-600" data-testid="cis-deduction-row"><span>Less CIS ({num(cisRate)}% on labour)</span><span className="font-mono">- {gbp(cisDeduction)}</span></div>
            )}
            {vatTreatment === "reverse_charge" ? (
              <div className="flex justify-between text-slate-500" data-testid="vat-row"><span>VAT — domestic reverse charge</span><span className="font-mono">{gbp(0)}</span></div>
            ) : vatTreatment && !["exempt", "zero"].includes(vatTreatment) ? (
              <div className="flex justify-between text-slate-600" data-testid="vat-row"><span>{VAT_LABELS[vatTreatment]}</span><span className="font-mono">+ {gbp(vatAmount)}</span></div>
            ) : null}
            <div className="flex justify-between items-center border-t border-slate-200 mt-1 pt-2">
              <span className="font-semibold text-slate-800">Amount due this period</span>
              <span className="font-mono text-lg font-bold text-blue-600" data-testid="amount-due">{gbp(due)}</span>
            </div>
          </div>
          {vatTreatment === "reverse_charge" && (
            <p className="text-xs text-slate-400 mt-2 italic" data-testid="reverse-charge-note">VAT reverse charge applies. Customer to account for VAT to HMRC.</p>
          )}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700">Billing note</label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="billing-note" className="mt-1" placeholder="Add a note for the application for payment, or generate one with AI." />
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle>Share Application for Payment</DialogTitle>
            <DialogDescription>Generate a read-only link your client can open in any browser — no login needed. It captures the billing as it stands now.</DialogDescription>
          </DialogHeader>
          {share ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 bg-slate-50 min-w-0">
                  <Link2 className="h-4 w-4 text-slate-400 shrink-0" />
                  <span className="text-sm text-slate-600 truncate" data-testid="share-url">{shareUrl}</span>
                </div>
                <Button size="sm" onClick={copyLink} data-testid="copy-share-btn" className="bg-blue-600 hover:bg-blue-700"><Copy className="h-4 w-4 mr-1.5" /> Copy</Button>
              </div>
              <div className="flex items-center justify-between text-sm text-slate-500">
                <span className="flex items-center gap-1.5" data-testid="share-views"><Eye className="h-4 w-4" /> {share.view_count} view{share.view_count === 1 ? "" : "s"}</span>
                <span>Amount due: £{(share.amount_due || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="afp-status-timeline">
                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Status</p>
                <div className="grid grid-cols-4 gap-1">
                  {[
                    { key: "sent", label: "Sent", done: !!share.sent_at, at: share.sent_at },
                    { key: "viewed", label: "Viewed", done: (share.view_count || 0) > 0, at: share.last_viewed_at },
                    { key: "approved", label: "Approved", done: !!share.approved, at: share.approved_at },
                    { key: "paid", label: "Paid", done: !!share.paid, at: share.paid_at },
                  ].map((s) => (
                    <div key={s.key} className="text-center" data-testid={`afp-step-${s.key}`}>
                      {s.done
                        ? <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" />
                        : <Circle className="h-5 w-5 text-slate-300 mx-auto" />}
                      <p className={`text-xs mt-1 ${s.done ? "text-slate-700 font-medium" : "text-slate-400"}`}>{s.label}</p>
                      {s.done && s.at && <p className="text-[10px] text-slate-400">{new Date(s.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</p>}
                    </div>
                  ))}
                </div>
              </div>

              <Button variant="outline" onClick={togglePaid} disabled={shareBusy} data-testid="mark-paid-btn" className={share.paid ? "w-full border-slate-300 text-slate-600" : "w-full border-green-300 text-green-700 hover:bg-green-50"}>
                {shareBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                {share.paid ? "Mark as unpaid" : "Mark as paid (BACS received)"}
              </Button>

              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={() => window.open(shareUrl, "_blank")} className="border-slate-300 flex-1 min-w-[90px]">Preview</Button>
                <Button variant="outline" onClick={generateShare} disabled={shareBusy} className="border-slate-300 flex-1 min-w-[90px]">Regenerate</Button>
                <Button variant="outline" onClick={revokeShare} disabled={shareBusy} data-testid="revoke-share-btn" className="border-red-200 text-red-600 flex-1 min-w-[90px]"><Ban className="h-4 w-4 mr-1.5" /> Revoke</Button>
              </div>
              <p className="text-xs text-slate-400">Regenerating updates the snapshot to your latest billing and issues a new link. Revoking disables the link immediately.</p>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-slate-500 mb-4">No active link yet. Create one to share this application for payment with your client.</p>
              <Button onClick={generateShare} disabled={shareBusy} data-testid="generate-share-btn" className="bg-blue-600 hover:bg-blue-700">
                {shareBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Share2 className="h-4 w-4 mr-1.5" />} Create share link
              </Button>
            </div>
          )}

          <div className="border-t border-slate-200 pt-4 mt-1">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><Mail className="h-4 w-4 text-slate-400" /> Email it to your client</label>
            <div className="flex items-center gap-2 mt-2">
              <Input type="email" placeholder="client@email.com" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} data-testid="client-email-input" className="flex-1" />
              <Button onClick={sendToClient} disabled={sending || !clientEmail} data-testid="send-client-btn" className="bg-blue-600 hover:bg-blue-700">
                {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />} Send
              </Button>
            </div>
            {share?.sent_to && <p className="text-xs text-slate-400 mt-2" data-testid="last-sent">Last emailed to {share.sent_to}</p>}
            <p className="text-xs text-slate-400 mt-1">We'll email a branded application with a secure view-and-pay link.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingLedger;
